import { PlanStatusEnum } from "../constants/enums";
import type { UserModel } from "../types/models";
import { getAge } from "../utils/date";
import {
  getIconColor,
  getLifeStyleStatus,
  getPrescriptionStatus,
  getTransparentBgColor,
  getVitalStatus,
  getVitalsBgColor,
} from "../utils/getVitalsStatus";

type PatientCardProps = {
  user: UserModel;
  include_vitals?: boolean;
  plan_status: PlanStatusEnum;
  onClick: () => void;
  actionLabel?: string;
};

const AdherenceDot = ({
  label,
  title,
  status,
}: {
  label: string;
  title: string;
  status?: ReturnType<typeof getVitalStatus>;
}) => (
  <span
    className="adherence-dot"
    data-tooltip={title}
    aria-label={title}
    style={{
      background: getTransparentBgColor(status),
      color: getIconColor(status),
      boxShadow: `inset 0 0 0 999px ${getVitalsBgColor(status)}`,
    }}
  >
    {label}
  </span>
);

export const PatientCard = ({
  user,
  include_vitals = false,
  plan_status,
  onClick,
  actionLabel,
}: PatientCardProps) => {
  const activePlan = user.plans?.[0];
  const statusClass =
    plan_status === PlanStatusEnum.ACTIVE
      ? "status-active"
      : plan_status === PlanStatusEnum.PENDING
        ? "status-pending"
        : "status-rejected";

  return (
    <button type="button" className={`patient-card ${statusClass}`} onClick={onClick}>
      <div className="patient-card-main">
        <div className="patient-card-title">
          {user.first_name} {user.last_name}
        </div>
        <div className="patient-card-meta">Age: {getAge(user.date_of_birth)}</div>
        <div className="patient-card-meta">{user.email}</div>
        {user.phone_number ? (
          <div className="patient-card-meta">{user.phone_number}</div>
        ) : null}
      </div>
      {include_vitals && activePlan?.data ? (
        <div className="patient-card-vitals">
          <AdherenceDot
            label="L"
            title="Lifestyle"
            status={getLifeStyleStatus(activePlan.data.lifestyle, user.vital_stats)}
          />
          <AdherenceDot
            label="V"
            title="Vitals"
            status={getVitalStatus(activePlan.data.vitals, user.vital_stats)}
          />
          <AdherenceDot
            label="P"
            title="Prescription"
            status={getPrescriptionStatus(
              activePlan.data.prescription,
              user.vital_stats
            )}
          />
        </div>
      ) : (
        <span className="pill">{actionLabel || plan_status}</span>
      )}
    </button>
  );
};
